import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, HostListener, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { AuthService } from '../../../core/auth/auth.service';
import {
  BOOKING_ACTION_ACCESS,
  canAccess,
  DEPARTMENT,
  ROLE_LEVEL,
} from '../../../core/auth/access-control';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import { COUNTRY_OPTIONS } from '../../../shared/constants/countries.constant';
import {
  BookingCommissionItem,
  BookingRefundItem,
  BookingListItem,
  BookingService,
  CustomerPaymentItem,
  ExpenseRequestItem,
  CreditCardFeePercent,
  CreditCardFeeResponsibility,
  PaymentMethod,
  PaymentStage,
} from '../services/booking.service';
import {
  FinancialAdjustmentItem,
  FinancialAdjustmentService,
} from '../services/financial-adjustment.service';
import { GroupBookingService } from '../services/group-booking.service';
import { BookingCollaboratorService } from '../services/booking-collaborator.service';
import { TourService, type TourFinancialAdjustmentItem } from '../../tours/services/tour.service';

interface BookingDetail {
  bookingNo: string;
  bookingType: BookingListItem['bookingType'];
  tourId: string | null;
  tourIsCompleted: boolean;
  customerCode: string;
  customerType: 'INDIVIDUAL' | 'CORPORATE';
  customerName: string;
  customerTaxId: string;
  customerPhone: string;
  customerEmail: string;
  programName: string;
  tourCode: string;
  countryName: string;
  wholesaleId: string | null;
  wholesaleName: string;
  saleId: string;
  saleName: string;
  createdById: string;
  saleRecoveryOutstanding: number;
  collaboratorId: string | null;
  collaboratorName: string;
  caretakerRecoveryOutstanding: number;
  tourParticipantIds: string[];
  createdByName: string;
  bookingDate: string;
  createdAt: string;
  departureDate: string;
  returnDate: string | null;
  travelerCount: number;
  totalAmount: number;
  pricePerPerson: number;
  discount: number;
  estimatedExpense: number;
  companyCommission: number | null;
  saleCommission: number | null;
  specialCommission: number | null;
  commissionAttachmentPath: string | null;
  paymentStatus: BookingListItem['paymentStatus'];
  status: BookingListItem['status'];
}

interface TourMember {
  id: string;
  travelerNo: number;
  fullName: string;
  citizenId: string;
  phoneNumber: string;
  passportFileName: string;
  isLeadTraveler: boolean;
  status: 'DRAFT' | 'COMPLETED' | 'DELETED';
}

interface BookingPayment {
  id: string;
  receiptNumber: string;
  paidAt: Date;
  payerName: string;
  paymentStage: PaymentStage;
  paymentMethod: PaymentMethod;
  amount: number;
  creditCardFeePercent: CreditCardFeePercent;
  creditCardFeeResponsibility: CreditCardFeeResponsibility | null;
  creditCardFeeAmount: number;
  customerPaidAmount: number;
  withholdingTaxAmount: number;
  slipFileName: string;
  receiptUrl: string;
  quotationUrl: string;
  status: CustomerPaymentItem['status'];
}

type BookingRefundStatus =
  | 'DRAFT'
  | 'SUPERVISOR_REVIEW'
  | 'WAITING_PAYMENT'
  | 'PAID'
  | 'REJECTED'
  | 'CANCELLED';

type BookingRefundType = 'PARTIAL' | 'FULL';

interface BookingRefund {
  id: string;
  refundNo: string;
  refundName: string;
  bankName: string;
  accountNumber: string;
  refundType: BookingRefundType;
  amount: number;
  reason: string;
  attachmentPath: string;
  transferSlipUrl: string;
  transferredAt: Date | null;
  status: BookingRefundStatus;
  paidAmount: number;
  createdAt: Date;
}

interface BookingExpense {
  id: string;
  code: string;
  description: string;
  expenseNameSnapshot: string;
  wholesaleId: string | null;
  wholesaleNameSnapshot: string;
  requester: string;
  amount: number | null;
  actualTransferAmount: number | null;
  source: ExpenseRequestItem['source'];
  purchaseOrderFileName: string;
  transferSlipUrl: string;
  receiptFileName: string;
  receiptSubmittedAt: Date | null;
  status: ExpenseRequestItem['status'];
  createdAt: Date;
  paidAmount: number;
}

interface BookingCommission {
  id: string;
  code: string;
  recipientRole: BookingCommissionItem['recipientRole'];
  recipientName: string;
  requesterName: string;
  pax: number;
  saleCommissionRate: number;
  specialCommissionRate: number;
  saleCommission: number;
  specialCommission: number;
  total: number;
  attachmentFileName: string;
  status: BookingCommissionItem['status'];
  createdAt: Date;
  submittedAt: Date | null;
  paidAmount: number;
  recoveries: CommissionRecovery[];
}

interface CommissionRecovery {
  id: string;
  refundNo: string;
  refundType: 'PARTIAL' | 'FULL';
  refundAmount: number;
  originalAmount: number;
  recoveryAmount: number;
  settledAmount: number;
  remainingAmount: number;
  status: 'PENDING' | 'PARTIAL' | 'SETTLED' | 'WAIVED' | 'CANCELLED';
  reason: string;
  createdAt: Date;
  applications: Array<{
    id: string;
    targetCode: string;
    targetType: 'BOOKING_COMMISSION' | 'TOUR_COMMISSION';
    amount: number;
    status: 'RESERVED' | 'APPLIED' | 'RELEASED';
    appliedAt: Date | null;
  }>;
}

@Component({
  selector: 'app-booking-detail-page',
  imports: [
    DatePipe,
    DecimalPipe,
    ReactiveFormsModule,
    RouterLink,
    LucideAngularModule,
    StatusBadge,
    RefreshButtonComponent,
    CheckboxModule,
    DatePickerModule,
    InputNumberModule,
    InputTextModule,
    RadioButtonModule,
    SelectModule,
    FileUploadModule,
  ],
  templateUrl: './booking-detail-page.component.html',
})
export class BookingDetailPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly bookingService = inject(BookingService);
  private readonly groupBookingService = inject(GroupBookingService);
  private readonly bookingCollaboratorService = inject(BookingCollaboratorService);
  private readonly financialAdjustmentService = inject(FinancialAdjustmentService);
  private readonly tourService = inject(TourService);
  private readonly overlay = inject(GlobalOverlayService);
  private readonly auth = inject(AuthService);

  readonly isRefreshButtonHidden = signal(true);

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.isRefreshButtonHidden.set(window.scrollY <= 16);
  }

  readonly canManageBooking = computed(
    () =>
      !this.isBookingDeleted() &&
      !this.isBookingLocked() &&
      canAccess(this.auth.user(), BOOKING_ACTION_ACCESS.manage),
  );
  readonly canDeleteRejectedBookingRecord = computed(
    () =>
      !this.isBookingDeleted() &&
      !this.isTourCompleted() &&
      canAccess(this.auth.user(), BOOKING_ACTION_ACCESS.manage),
  );
  readonly canRequestCommission = computed(() => {
    const user = this.auth.user();
    const status = this.booking()?.status;
    return !this.isBookingDeleted() && !this.isTourCompleted() && !['REFUND_PENDING', 'REFUND'].includes(status ?? '') && (
      user?.role.level === ROLE_LEVEL.ADMIN ||
      canAccess(user, BOOKING_ACTION_ACCESS.manage)
    );
  });
  readonly canViewCreateRefundAction = computed(() => {
    const user = this.auth.user();
    const status = this.booking()?.status;
    return (
      !this.isBookingDeleted() &&
      !this.isTourCompleted() &&
      !['REFUND_PENDING', 'REFUND'].includes(status ?? '') &&
      (user?.role.level === ROLE_LEVEL.ADMIN ||
        canAccess(user, BOOKING_ACTION_ACCESS.manage))
    );
  });
  readonly canCreateRefund = computed(
    () =>
      this.canViewCreateRefundAction() &&
      !this.isBookingDeleted() &&
      this.verifiedCustomerPaidTotal() > 0 &&
      !this.hasPendingRefund() &&
      this.remainingFullRefundAmount() > 0,
  );
  readonly canViewExpenseSection = computed(
    () => this.auth.user()?.role.level !== ROLE_LEVEL.EMPLOYEE_ASSISTANT,
  );
  readonly canViewFullFinancialSummary = computed(() =>
    canAccess(this.auth.user(), {
      anyOf: [
        { departments: [DEPARTMENT.ACCOUNTING] },
        { levels: [ROLE_LEVEL.EMPLOYEE], departments: [DEPARTMENT.SALES] },
      ],
    }),
  );
  readonly canCreateFinancialAdjustment = computed(
    () =>
      !this.isBookingDeleted() &&
      !this.isBookingLocked() &&
      canAccess(this.auth.user(), {
        levels: [ROLE_LEVEL.MANAGER, ROLE_LEVEL.EMPLOYEE],
        departments: [DEPARTMENT.SALES],
      }),
  );
  readonly canViewFinancialAdjustments = computed(
    () =>
      this.canViewFullFinancialSummary() ||
      canAccess(this.auth.user(), {
        levels: [ROLE_LEVEL.MANAGER, ROLE_LEVEL.EMPLOYEE],
        departments: [DEPARTMENT.SALES],
      }),
  );

  readonly bookingId = this.route.snapshot.paramMap.get('id') ?? '';
  private readonly fromParam = this.route.snapshot.queryParamMap.get('from');
  private readonly fromTourId = this.route.snapshot.queryParamMap.get('tourId');
  readonly isGroupBookingRoute = this.fromParam === 'group-bookings';
  readonly booking = signal<BookingDetail | null>(null);
  readonly isBookingCommissionDocumentModalOpen = signal(false);
  readonly bookingCommissionDocumentFile = signal<File | null>(null);
  readonly isSavingBookingCommissionDocument = signal(false);
  selectBookingCommissionDocument(event: FileSelectEvent): void {
    if (!this.canManageBooking()) return;
    const file = event.files[0];
    if (!file || file.size > 5_000_000 || !/\.(pdf|jpe?g|png)$/i.test(file.name)) return;
    this.bookingCommissionDocumentFile.set(file);
  }

  clearBookingCommissionDocument(): void {
    if (!this.canManageBooking()) return;
    this.bookingCommissionDocumentFile.set(null);
  }

  commissionAttachmentUrl(path: string): string {
    return this.bookingService.resolveFileUrl(path);
  }

  closeBookingCommissionDocumentModal(): void {
    this.isBookingCommissionDocumentModalOpen.set(false);
  }

  async saveBookingCommissionDocument(): Promise<void> {
    const file = this.bookingCommissionDocumentFile();
    if (!file || this.isSavingBookingCommissionDocument()) return;

    this.isSavingBookingCommissionDocument.set(true);
    try {
      await this.bookingService.saveCommissionAttachment(this.bookingId, file);
      this.bookingCommissionDocumentFile.set(null);
      this.closeBookingCommissionDocumentModal();
      await this.loadBooking();
      this.overlay.success('บันทึกเอกสารรับค่าคอมสำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('บันทึกเอกสารรับค่าคอมไม่สำเร็จ', message);
    } finally {
      this.isSavingBookingCommissionDocument.set(false);
    }
  }

  readonly bookingListUrl = computed(() => {
    if (this.fromTourId) return `/tours/${this.fromTourId}`;
    if (this.isGroupBookingRoute) return '/bookings/group';
    if (this.fromParam === 'bookings') return '/bookings';

    const tourId = this.booking()?.tourId;
    return tourId ? `/tours/${tourId}` : '/bookings';
  });
  readonly isBookingDeleted = computed(() => this.booking()?.status === 'DELETED');
  readonly isBookingCompleted = computed(() => this.booking()?.status === 'COMPLETED');
  readonly isTourCompleted = computed(() => this.booking()?.tourIsCompleted ?? false);
  readonly isBookingLocked = computed(() =>
    this.isTourCompleted() ||
    ['COMPLETED', 'REFUND_PENDING', 'REFUND'].includes(this.booking()?.status ?? ''),
  );
  readonly canCloseBookingCase = computed(() => {
    const user = this.auth.user();
    const booking = this.booking();
    if (!user || !booking || this.isTourCompleted() || user.department.code === DEPARTMENT.ACCOUNTING) return false;
    return (
      user.role.level === ROLE_LEVEL.ADMIN ||
      ((user.role.level === ROLE_LEVEL.MANAGER || user.role.level === ROLE_LEVEL.EMPLOYEE) &&
        user.department.code === DEPARTMENT.SALES) ||
      booking.collaboratorId === user.id
    );
  });
  readonly canViewCloseBookingCase = computed(() => {
    const user = this.auth.user();
    const booking = this.booking();
    if (!user || !booking || this.isTourCompleted() || user.department.code === DEPARTMENT.ACCOUNTING) return false;
    return (
      user.role.level === ROLE_LEVEL.ADMIN ||
      ((user.role.level === ROLE_LEVEL.MANAGER || user.role.level === ROLE_LEVEL.EMPLOYEE) &&
        user.department.code === DEPARTMENT.SALES) ||
      booking.collaboratorId === user.id
    );
  });
  readonly canCloseGroupBookingCase = computed(() => {
    const booking = this.booking();
    return Boolean(
      booking &&
        booking.bookingType === 'GROUP_TOUR' &&
        booking.status === 'CONFIRMED' &&
        this.canCloseBookingCase(),
    );
  });
  readonly isClosingGroupBookingCase = signal(false);
  readonly canCloseJoinBookingCase = computed(() => {
    const booking = this.booking();
    return Boolean(
      booking &&
        booking.bookingType === 'JOIN_TOUR' &&
        booking.status === 'CONFIRMED' &&
        this.canCloseBookingCase(),
    );
  });
  readonly isClosingJoinBookingCase = signal(false);
  // ทัวร์ใน: booking ผูกกับทัวร์ของบริษัท → ค่าใช้จ่าย/วงเงินบริหารที่ระดับทัวร์แทน
  readonly isInHouseTour = computed(() => !!this.booking()?.tourId);
  readonly isLoading = signal(true);
  readonly isRefreshing = signal(false);
  readonly isNotFound = signal(false);
  readonly isCollaboratorModalOpen = signal(false);
  readonly isLoadingSalesStaff = signal(false);
  readonly isSavingCollaborator = signal(false);
  readonly salesStaffOptions = signal<Array<{ label: string; value: string }>>([]);
  readonly collaboratorForm = this.fb.group({
    collaboratorId: [null as string | null, Validators.required],
  });

  private activeBookingService(): BookingService | GroupBookingService {
    return this.isGroupBookingRoute || this.booking()?.bookingType === 'GROUP_TOUR'
      ? this.groupBookingService
      : this.bookingService;
  }

  closeBookingCaseButtonTitle(): string {
    const booking = this.booking();
    if (booking?.status === 'COMPLETED') return 'Booking ปิดเคสแล้ว';
    if (booking?.status === 'REFUND_PENDING') return 'Booking อยู่ระหว่างคืนเงิน';
    if (booking?.status === 'REFUND') return 'Booking คืนเงินเสร็จสิ้นแล้ว';
    if (booking?.status !== 'CONFIRMED') return 'ต้องยืนยันการจองก่อนปิดเคส';
    if (!this.canCloseBookingCase()) return 'เฉพาะผู้ดูแลหรือสมาชิกทัวร์เท่านั้นที่ปิดเคสได้';
    return 'ปิดเคส';
  }

  readonly bookingTypeLabel = computed(() =>
    this.booking()?.bookingType === 'GROUP_TOUR' ? 'ทัวร์เหมากรุ๊ป' : 'จอยทัวร์',
  );
  readonly grossTourAmount = computed(() => {
    const booking = this.booking();
    return booking ? booking.pricePerPerson * booking.travelerCount : 0;
  });
  readonly totalDiscount = computed(() => {
    const booking = this.booking();
    return booking?.discount ?? 0;
  });
  readonly tourMembers = signal<TourMember[]>([]);
  readonly tourMemberLimit = computed(() =>
    this.booking()?.bookingType === 'GROUP_TOUR' ? 1 : (this.booking()?.travelerCount ?? 0),
  );
  readonly isTourMemberLimitReached = computed(() => {
    const memberLimit = this.tourMemberLimit();
    return memberLimit > 0 && this.tourMembers().length >= memberLimit;
  });
  readonly maxPassportFileSize = computed(() =>
    this.booking()?.bookingType === 'GROUP_TOUR' ? 20 * 1024 * 1024 : 5_000_000,
  );
  readonly maxPassportFileSizeLabel = computed(() =>
    this.booking()?.bookingType === 'GROUP_TOUR' ? '20 MB' : '5 MB',
  );

  readonly isMemberModalOpen = signal(false);
  readonly editingTourMember = signal<TourMember | null>(null);
  readonly isSavingMember = signal(false);
  readonly passportFile = signal<File | null>(null);
  readonly passportFileName = signal('');
  readonly memberForm = this.fb.group({
    fullName: ['', Validators.required],
    citizenId: ['', [Validators.required, Validators.minLength(9), Validators.maxLength(13)]],
    phoneNumber: ['', [Validators.required, Validators.pattern(/^0\d{8,9}$/)]],
    isLeadTraveler: [false],
  });

  readonly payments = signal<BookingPayment[]>([]);

  readonly refunds = signal<BookingRefund[]>([]);
  readonly isRefundModalOpen = signal(false);
  readonly isSavingRefund = signal(false);
  readonly refundAttachmentFile = signal<File | null>(null);
  readonly refundAttachmentFileName = signal('');
  readonly refundTypeOptions: Array<{ label: string; value: BookingRefundType }> = [
    { label: 'คืนบางส่วน', value: 'PARTIAL' },
    { label: 'คืนทั้งหมด', value: 'FULL' },
  ];
  readonly bankOptions: Array<{ label: string; value: string }> = [
    { label: 'ธนาคารกรุงเทพ', value: 'ธนาคารกรุงเทพ' },
    { label: 'ธนาคารกสิกรไทย', value: 'ธนาคารกสิกรไทย' },
    { label: 'ธนาคารกรุงไทย', value: 'ธนาคารกรุงไทย' },
    { label: 'ธนาคารไทยพาณิชย์', value: 'ธนาคารไทยพาณิชย์' },
    { label: 'ธนาคารกรุงศรีอยุธยา', value: 'ธนาคารกรุงศรีอยุธยา' },
    { label: 'ธนาคารทหารไทยธนชาต', value: 'ธนาคารทหารไทยธนชาต' },
    { label: 'ธนาคารออมสิน', value: 'ธนาคารออมสิน' },
    { label: 'ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร', value: 'ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร' },
    { label: 'ธนาคารอาคารสงเคราะห์', value: 'ธนาคารอาคารสงเคราะห์' },
    { label: 'ธนาคารซีไอเอ็มบี ไทย', value: 'ธนาคารซีไอเอ็มบี ไทย' },
    { label: 'ธนาคารยูโอบี', value: 'ธนาคารยูโอบี' },
    { label: 'ธนาคารเกียรตินาคินภัทร', value: 'ธนาคารเกียรตินาคินภัทร' },
    { label: 'ธนาคารทิสโก้', value: 'ธนาคารทิสโก้' },
    { label: 'ธนาคารไอซีบีซี (ไทย)', value: 'ธนาคารไอซีบีซี (ไทย)' },
  ];
  readonly refundForm = this.fb.group({
    refundName: ['', [Validators.required, Validators.maxLength(150)]],
    bankName: [null as string | null, Validators.required],
    accountNumber: ['', [Validators.required, Validators.pattern(/^\d{6,20}$/)]],
    refundType: [null as BookingRefundType | null, Validators.required],
    amount: [null as number | null, [Validators.required, Validators.min(1)]],
    reason: ['', [Validators.required, Validators.maxLength(500)]],
  });

  readonly isPaymentModalOpen = signal(false);
  readonly editingPayment = signal<BookingPayment | null>(null);
  readonly isSavingPayment = signal(false);
  readonly paymentSlipFile = signal<File | null>(null);
  readonly paymentSlipFileName = signal('');
  readonly paymentTypeOptions: Array<{ label: string; value: PaymentMethod }> = [
    { label: 'โอนเงินผ่านธนาคาร', value: 'BANK_TRANSFER' },
    { label: 'เงินสด', value: 'CASH' },
    { label: 'บัตรเครดิต/เดบิต', value: 'CREDIT_CARD' },
  ];
  readonly paymentStageOptions: Array<{ label: string; value: PaymentStage }> = [
    { label: 'ค่าจอง', value: 'BOOKING' },
    { label: 'เงินมัดจำ', value: 'DEPOSIT' },
    { label: 'ยอดคงเหลือ', value: 'BALANCE' },
    { label: 'ชำระเต็มจำนวน', value: 'FULL_PAYMENT' },
  ];
  readonly creditCardFeeOptions: Array<{ label: string; value: CreditCardFeePercent }> = [
    { label: '0%', value: 0 },
    { label: '3%', value: 3 },
    { label: '4%', value: 4 },
  ];
  readonly creditCardFeeResponsibilityOptions: Array<{
    label: string;
    value: CreditCardFeeResponsibility;
  }> = [
    { label: 'ลูกค้าชำระ', value: 'CUSTOMER' },
    { label: 'บริษัทรับภาระ', value: 'COMPANY' },
  ];
  readonly paymentForm = this.fb.group({
    payerName: ['', Validators.required],
    paymentStage: [null as PaymentStage | null, Validators.required],
    paymentType: [null as PaymentMethod | null, Validators.required],
    creditCardFeePercent: [null as CreditCardFeePercent | null],
    creditCardFeeResponsibility: [null as CreditCardFeeResponsibility | null],
    amount: [null as number | null, [Validators.required, Validators.min(1)]],
    hasWithholdingTax: [false],
    withholdingTaxAmount: [0, [Validators.required, Validators.min(0)]],
    paidDate: [null as Date | null, Validators.required],
    paidTime: [null as Date | null, Validators.required],
  });
  private readonly selectedPaymentType = toSignal(
    this.paymentForm.controls.paymentType.valueChanges,
    { initialValue: this.paymentForm.controls.paymentType.value },
  );
  private readonly selectedCreditCardFeePercent = toSignal(
    this.paymentForm.controls.creditCardFeePercent.valueChanges,
    { initialValue: this.paymentForm.controls.creditCardFeePercent.value },
  );
  private readonly selectedCreditCardFeeResponsibility = toSignal(
    this.paymentForm.controls.creditCardFeeResponsibility.valueChanges,
    { initialValue: this.paymentForm.controls.creditCardFeeResponsibility.value },
  );

  readonly expenses = signal<BookingExpense[]>([]);
  readonly isExpenseModalOpen = signal(false);
  readonly isSavingExpense = signal(false);
  readonly editingExpense = signal<BookingExpense | null>(null);
  readonly purchaseOrderFile = signal<File | null>(null);
  readonly purchaseOrderFileName = signal('');
  readonly selectedReceiptExpense = signal<BookingExpense | null>(null);
  readonly expenseReceiptFile = signal<File | null>(null);
  readonly expenseReceiptFileName = signal('');
  readonly isSavingExpenseReceipt = signal(false);
  readonly expenseTypeOptions: string[] = [];
  readonly wholesaleOptions = signal<Array<{ label: string; value: string }>>([]);
  readonly expenseForm = this.fb.group({
    description: ['', Validators.required],
    expenseNameSnapshot: ['', Validators.required],
    wholesaleId: [''],
    amount: [{ value: null as number | null, disabled: true }],
    actualTransferAmount: [null as number | null, [Validators.required, Validators.min(1)]],
  });

  readonly commissions = signal<BookingCommission[]>([]);
  readonly isCommissionModalOpen = signal(false);
  readonly editingCommission = signal<BookingCommission | null>(null);
  readonly commissionRecipientRole = signal<BookingCommissionItem['recipientRole']>('SALE');
  readonly isSavingCommission = signal(false);
  readonly selectedCommissionRecovery = signal<BookingCommission | null>(null);
  readonly commissionForm = this.fb.group({
    saleCommission: [null as number | null, [Validators.required, Validators.min(0)]],
    specialCommission: [null as number | null, [Validators.required, Validators.min(0)]],
  });

  readonly isFinancialAdjustmentModalOpen = signal(false);
  readonly isSavingFinancialAdjustment = signal(false);
  readonly isLoadingFinancialAdjustments = signal(false);
  readonly financialAdjustments = signal<FinancialAdjustmentItem[]>([]);
  readonly tourFinancialAdjustments = signal<TourFinancialAdjustmentItem[]>([]);
  readonly approvedFinancialAdjustmentTotals = signal({
    expenseBudgetDelta: 0,
    customerChargeDelta: 0,
    companyAdvanceAmount: 0,
  });
  readonly expenseAvailability = signal({
    verifiedTotal: 0,
    approvedCompanyAdvance: 0,
    expenseTotal: 0,
    availableAmount: 0,
  });
  readonly financialAdjustmentSubmitted = signal(false);
  readonly advanceAttachmentFile = signal<File | null>(null);
  readonly advanceAttachmentFileName = signal('');
  readonly financialAdjustmentForm = this.fb.group({
    expenseBudgetDelta: [null as number | null, Validators.min(0)],
    customerChargeDelta: [null as number | null, Validators.min(0)],
    companyAdvanceAmount: [null as number | null, Validators.min(0)],
    reason: ['', [Validators.required, Validators.maxLength(500)]],
  });

  readonly bookingTotal = computed(() => this.booking()?.totalAmount ?? 0);
  readonly approvedExpenseBudgetDelta = computed(
    () => this.approvedFinancialAdjustmentTotals().expenseBudgetDelta,
  );
  readonly approvedCustomerChargeDelta = computed(
    () => this.approvedFinancialAdjustmentTotals().customerChargeDelta,
  );
  readonly approvedTourPriceAmount = computed(() => {
    const booking = this.booking();
    if (!booking?.tourId) return 0;

    return this.tourFinancialAdjustments()
      .filter((adjustment) => adjustment.status === 'APPROVED')
      .reduce((total, adjustment) => total + this.toNumber(adjustment.tourPriceDelta), 0);
  });
  readonly approvedCompanyAdvanceAmount = computed(
    () => this.approvedFinancialAdjustmentTotals().companyAdvanceAmount,
  );
  readonly adjustedBookingTotal = computed(
    () => this.bookingTotal() + this.approvedCustomerChargeDelta(),
  );
  readonly adjustedExpenseBudget = computed(
    () => (this.booking()?.estimatedExpense ?? 0) + this.approvedExpenseBudgetDelta(),
  );
  readonly employeeCommissionTotal = computed(() =>
    this.commissions()
      .filter((commission) => commission.status === 'VERIFIED')
      .reduce((total, commission) => total + commission.total, 0),
  );
  readonly adjustedRemainingBalance = computed(() =>
    this.isVerifiedPaymentComplete()
      ? 0
      : Math.max(this.adjustedBookingTotal() - this.verifiedPaymentTotal(), 0),
  );
  readonly verifiedPaymentRemaining = computed(() =>
    this.isVerifiedPaymentComplete()
      ? 0
      : Math.max(this.adjustedBookingTotal() - this.verifiedPaymentTotal(), 0),
  );
  readonly paidTotal = computed(() =>
    this.payments()
      .filter((payment) => !['REJECTED', 'CANCELLED', 'DELETED'].includes(payment.status))
      .reduce((total, payment) => total + payment.amount + payment.withholdingTaxAmount, 0),
  );
  readonly verifiedPaymentTotal = computed(() =>
    this.payments()
      .filter((payment) => payment.status === 'VERIFIED')
      .reduce((total, payment) => total + payment.amount + payment.withholdingTaxAmount, 0),
  );
  readonly verifiedCustomerPaidTotal = computed(() =>
    this.payments()
      .filter((payment) => payment.status === 'VERIFIED')
      .reduce((total, payment) => total + payment.customerPaidAmount, 0),
  );
  readonly verifiedWithholdingTaxTotal = computed(() =>
    this.payments()
      .filter((payment) => payment.status === 'VERIFIED')
      .reduce((total, payment) => total + payment.withholdingTaxAmount, 0),
  );
  readonly isVerifiedPaymentComplete = computed(
    () => this.verifiedPaymentTotal() >= Math.max(this.adjustedBookingTotal() - 1, 0),
  );
  readonly verifiedCustomerCreditCardFeeTotal = computed(() =>
    this.payments()
      .filter(
        (payment) =>
          payment.status === 'VERIFIED' && payment.creditCardFeeResponsibility === 'CUSTOMER',
      )
      .reduce((total, payment) => total + payment.creditCardFeeAmount, 0),
  );
  readonly verifiedCompanyCreditCardFeeTotal = computed(() =>
    this.payments()
      .filter(
        (payment) =>
          payment.status === 'VERIFIED' && payment.creditCardFeeResponsibility === 'COMPANY',
      )
      .reduce((total, payment) => total + payment.creditCardFeeAmount, 0),
  );
  readonly financialSummary = computed(() => ({
    netSales: this.adjustedBookingTotal(),
    verifiedPayment: Math.max(this.verifiedCustomerPaidTotal() - this.paidRefundTotal(), 0),
    estimatedExpense: this.adjustedExpenseBudget(),
    actualProfit: this.actualProfit(),
  }));
  readonly activeExpenseTotal = computed(() =>
    this.expenses()
      .filter((expense) => !['REJECTED', 'CANCELLED', 'DELETED'].includes(expense.status))
      .reduce((total, expense) => total + this.expenseEffectiveAmount(expense), 0),
  );
  readonly paidExpenseTotal = computed(() =>
    this.expenses().reduce((total, expense) => total + expense.paidAmount, 0),
  );
  readonly expenseBudgetRemaining = computed(() =>
    Math.max(this.adjustedExpenseBudget() - this.activeExpenseTotal(), 0),
  );
  readonly expenseBudgetShortfall = computed(() =>
    Math.max(this.activeExpenseTotal() - this.adjustedExpenseBudget(), 0),
  );
  readonly spentTotal = computed(
    () =>
      this.expenses().reduce((total, expense) => total + expense.paidAmount, 0) +
      this.commissions().reduce((total, commission) => total + commission.paidAmount, 0),
  );
  readonly activeRefundTotal = computed(() =>
    this.refunds()
      .filter((refund) => !['REJECTED', 'CANCELLED'].includes(refund.status))
      .reduce((total, refund) => total + refund.amount, 0),
  );
  readonly paidRefundTotal = computed(() =>
    this.refunds()
      .filter((refund) => refund.status === 'PAID')
      .reduce((total, refund) => total + refund.paidAmount, 0),
  );
  readonly hasPendingRefund = computed(() =>
    this.refunds().some((refund) =>
      ['DRAFT', 'SUPERVISOR_REVIEW', 'WAITING_PAYMENT'].includes(refund.status),
    ),
  );
  readonly remainingFullRefundAmount = computed(() =>
    Math.max(this.verifiedCustomerPaidTotal() - this.activeRefundTotal(), 0),
  );
  readonly availableRefundAmount = computed(() =>
    Math.max(
      this.verifiedCustomerPaidTotal() - this.spentTotal() - this.activeRefundTotal(),
      0,
    ),
  );
  readonly actualProfit = computed(
    () =>
      this.verifiedPaymentTotal() -
      this.verifiedWithholdingTaxTotal() -
      this.spentTotal() -
      this.activeRefundTotal() -
      this.verifiedCompanyCreditCardFeeTotal(),
  );
  readonly availableExpenseAmount = computed(() => this.expenseAvailability().availableAmount);
  readonly hasActiveCommission = computed(() =>
    this.commissions().some(
      (commission) => !['REJECTED', 'CANCELLED', 'DELETED'].includes(commission.status),
    ),
  );
  readonly hasBlockingCommissionForExpense = computed(() =>
    this.booking()?.bookingType === 'JOIN_TOUR'
      ? this.commissions().some((commission) => commission.status === 'VERIFIED')
      : this.hasActiveCommission(),
  );
  readonly canCreateExpense = computed(
    () =>
      !this.isBookingDeleted() &&
      !this.isBookingLocked() &&
      !this.hasBlockingCommissionForExpense() &&
      this.availableExpenseAmount() > 0 &&
      this.expenseBudgetRemaining() > 0,
  );
  readonly isCommissionPaymentComplete = computed(
    () => this.verifiedPaymentTotal() >= Math.max(this.bookingTotal() - 1, 0),
  );
  readonly commissionRecipientName = computed(() => {
    const booking = this.booking();
    return this.commissionRecipientRole() === 'CARETAKER'
      ? booking?.collaboratorName || 'ยังไม่ได้กำหนดผู้ดูแล'
      : booking?.saleName || 'ยังไม่ได้กำหนดพนักงานขาย';
  });
  readonly commissionBaseAmount = computed(() => {
    const booking = this.booking();
    return this.commissionRecipientRole() === 'CARETAKER'
      ? 50
      : (booking?.saleCommission ?? 0) + (booking?.specialCommission ?? 0);
  });
  readonly commissionPreviewTotal = computed(() =>
    this.commissionRecipientRole() === 'SALE'
      ? this.commissionBaseAmount()
      : this.commissionBaseAmount() * Math.max(this.booking()?.travelerCount ?? 1, 1),
  );
  readonly saleCommissionPerPerson = computed(
    () =>
      (this.booking()?.saleCommission ?? 0) /
      Math.max(this.booking()?.travelerCount ?? 1, 1),
  );
  readonly specialCommissionPerPerson = computed(
    () =>
      (this.booking()?.specialCommission ?? 0) /
      Math.max(this.booking()?.travelerCount ?? 1, 1),
  );
  readonly commissionRecoveryOutstanding = computed(() => {
    const booking = this.booking();
    if (!booking) return 0;
    return this.commissionRecipientRole() === 'SALE'
      ? booking.saleRecoveryOutstanding
      : booking.caretakerRecoveryOutstanding;
  });
  readonly commissionRecoveryDeduction = computed(() =>
    Math.min(this.commissionPreviewTotal(), this.commissionRecoveryOutstanding()),
  );
  readonly commissionNetPaymentPreview = computed(() =>
    this.commissionPreviewTotal() - this.commissionRecoveryDeduction(),
  );
  readonly activeCommissionTotal = computed(() =>
    this.commissions()
      .filter((commission) => !['REJECTED', 'CANCELLED', 'DELETED'].includes(commission.status))
      .reduce((total, commission) => total + commission.total, 0),
  );
  readonly availableCommissionAmount = computed(() =>
    Math.max(
      this.verifiedPaymentTotal() -
        this.activeExpenseTotal() -
        this.activeCommissionTotal() +
        (this.editingCommission()?.total ?? 0),
      0,
    ),
  );
  readonly remainingBalance = computed(() =>
    this.paidTotal() >= Math.max(this.adjustedBookingTotal() - 1, 0)
      ? 0
      : Math.max(this.adjustedBookingTotal() - this.paidTotal(), 0),
  );
  readonly paymentAmountLimit = computed(() => {
    const baseLimit = this.remainingBalance() + (this.editingPayment()?.amount ?? 0);
    const customerPaysCreditCardFee =
      this.selectedPaymentType() === 'CREDIT_CARD' &&
      this.selectedCreditCardFeeResponsibility() === 'CUSTOMER';
    if (!customerPaysCreditCardFee) return baseLimit;

    const feePercent = this.selectedCreditCardFeePercent() ?? 0;
    return Math.round(baseLimit * (1 + feePercent / 100) * 100) / 100;
  });
  private readonly syncPaymentAmountValidator = effect(() => {
    this.paymentAmountLimit();
    this.setPaymentAmountValidator();
  });

  ngOnInit(): void {
    void this.refreshBookingDetails();
  }

  async refreshBookingDetails(): Promise<void> {
    if (this.isRefreshing()) return;
    this.isRefreshing.set(true);
    try {
      await Promise.all([
        this.loadExpenseTypes(),
        this.loadWholesales(),
        this.loadBooking(),
        this.loadFinancialAdjustments(),
      ]);
    } finally {
      this.isRefreshing.set(false);
    }
  }

  async closeGroupBookingCase(): Promise<void> {
    const booking = this.booking();
    if (!booking || !this.canCloseGroupBookingCase() || this.isClosingGroupBookingCase()) return;

    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการปิดเคส',
      message: 'ระบบจะตรวจสอบสมาชิกทัวร์และยอดชำระเงินก่อนปิดเคส',
      confirmText: 'ปิดเคส',
      cancelText: 'ยกเลิก',
    });
    if (!confirmed) return;

    this.isClosingGroupBookingCase.set(true);
    this.overlay.showLoading('กำลังตรวจสอบและปิดเคส...');
    try {
      await this.groupBookingService.closeCase(this.bookingId);
      await this.loadBooking();
      this.overlay.success('ปิดเคส Booking เหมากรุ๊ปสำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('ปิดเคส Booking เหมากรุ๊ปไม่สำเร็จ', message);
    } finally {
      this.isClosingGroupBookingCase.set(false);
      this.overlay.hideLoading();
    }
  }

  async closeJoinBookingCase(): Promise<void> {
    const booking = this.booking();
    if (
      !booking ||
      !this.canCloseJoinBookingCase() ||
      this.isClosingJoinBookingCase()
    ) return;

    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการปิดเคส',
      message: booking.tourId
        ? 'ระบบจะตรวจสอบสมาชิกทัวร์และยอดชำระของ Booking ก่อนปิดเคส'
        : 'ระบบจะตรวจสอบสมาชิกทัวร์ ยอดชำระ คำขอปรับวงเงิน เงินสำรอง และรายการเบิกจ่ายก่อนปิดเคส',
      confirmText: 'ปิดเคส',
      cancelText: 'ยกเลิก',
    });
    if (!confirmed) return;

    this.isClosingJoinBookingCase.set(true);
    this.overlay.showLoading('กำลังตรวจสอบและปิดเคส...');
    try {
      await this.bookingService.closeJoinCase(this.bookingId);
      await this.loadBooking();
      this.overlay.success('ปิดเคสจอยทัวร์สำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('ปิดเคสจอยทัวร์ไม่สำเร็จ', message);
    } finally {
      this.isClosingJoinBookingCase.set(false);
      this.overlay.hideLoading();
    }
  }

  private async loadFinancialAdjustments(): Promise<void> {
    if (!this.bookingId || !this.canViewFinancialAdjustments()) return;

    this.isLoadingFinancialAdjustments.set(true);
    try {
      this.financialAdjustments.set(
        await this.financialAdjustmentService.getByBookingId(this.bookingId),
      );
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('โหลดรายการปรับวงเงินไม่สำเร็จ', message);
    } finally {
      this.isLoadingFinancialAdjustments.set(false);
    }
  }

  private async loadExpenseTypes(): Promise<void> {
    if (this.isGroupBookingRoute) {
      this.expenseTypeOptions.splice(0, this.expenseTypeOptions.length);
      return;
    }
    try {
      const expenseTypes = await this.bookingService.getExpenseTypes();
      const optionNames = expenseTypes
        .filter((expenseType) => expenseType.name !== 'ค่าใช้จ่ายโฮลเซล')
        .map((expenseType) => expenseType.name);
      this.expenseTypeOptions.splice(0, this.expenseTypeOptions.length, ...optionNames);
    } catch {
      this.overlay.error('โหลดประเภทค่าใช้จ่ายไม่สำเร็จ', 'กรุณาลองใหม่อีกครั้ง');
    }
  }

  private async loadWholesales(): Promise<void> {
    if (this.isGroupBookingRoute) {
      this.wholesaleOptions.set([]);
      return;
    }
    try {
      const wholesales = await this.bookingService.getWholesales();
      this.wholesaleOptions.set(
        wholesales.map((wholesale) => ({ label: wholesale.name, value: wholesale.id })),
      );
    } catch {
      this.overlay.error('โหลดรายการโฮลเซลไม่สำเร็จ', 'กรุณาลองใหม่อีกครั้ง');
    }
  }

  private async loadBooking(): Promise<void> {
    if (!this.bookingId) {
      this.isNotFound.set(true);
      this.isLoading.set(false);
      return;
    }

    this.overlay.showLoading('กำลังโหลดรายละเอียด Booking...');
    try {
      const booking = await this.activeBookingService().getById(this.bookingId);
      if (!booking) {
        this.isNotFound.set(true);
        return;
      }

      this.booking.set({
        bookingNo: booking.bookingNo,
        bookingType: booking.bookingType,
        tourId: booking.tourId,
        tourIsCompleted: booking.tour?.status === 'COMPLETED',
        customerCode: booking.booking_customer.customerCode,
        customerType: booking.booking_customer.customerType,
        customerName: booking.booking_customer.name,
        customerTaxId: booking.booking_customer.taxId ?? '',
        customerPhone: booking.booking_customer.phone ?? '',
        customerEmail: booking.booking_customer.email ?? '',
        programName: booking.tourName,
        tourCode: booking.tourCode ?? '',
        countryName:
          COUNTRY_OPTIONS.find((country) => country.value === booking.country)?.label ??
          booking.country ??
          '',
        wholesaleId: booking.wholesaleId,
        wholesaleName: booking.booking_wholesale?.name ?? '',
        saleId: booking.salebookings.id,
        saleName: `${booking.salebookings.firstName} ${booking.salebookings.lastName}`.trim(),
        createdById: booking.createdBy.id,
        saleRecoveryOutstanding: this.toNumber(
          booking.commissionRecoveryBalances?.find(
            (balance) => balance.recipientUserId === booking.salebookings.id,
          )?.outstandingAmount,
        ),
        collaboratorId: booking.collaboratorId,
        collaboratorName: booking.collaborator
          ? `${booking.collaborator.firstName} ${booking.collaborator.lastName}`.trim()
          : '',
        caretakerRecoveryOutstanding: this.toNumber(
          booking.commissionRecoveryBalances?.find(
            (balance) => balance.recipientUserId === booking.collaboratorId,
          )?.outstandingAmount,
        ),
        tourParticipantIds: booking.tour
          ? [booking.tour.createdById, ...booking.tour.administrators.map((administrator) => administrator.userId)]
          : [],
        createdByName: `${booking.createdBy.firstName} ${booking.createdBy.lastName}`.trim(),
        bookingDate: booking.bookingDate,
        createdAt: booking.createdAt,
        departureDate: booking.departureDate,
        returnDate: booking.returnDate,
        travelerCount: booking.pax,
        pricePerPerson: this.toNumber(booking.pricePerPerson),
        totalAmount: this.toNumber(booking.totalAmount),
        paymentStatus: booking.paymentStatus,
        status: booking.status,
        discount: this.toNumber(booking.discount),
        estimatedExpense: this.toNumber(booking.estimatedExpense),
        companyCommission: booking.com_company === null ? null : this.toNumber(booking.com_company),
        saleCommission: booking.com_sale === null ? null : this.toNumber(booking.com_sale),
        specialCommission: booking.com_special === null ? null : this.toNumber(booking.com_special),
        commissionAttachmentPath: booking.commissionAttachmentPath,
      });
      await this.loadTourFinancialAdjustments(booking.tourId);
      const approvedTotals = booking.approvedFinancialAdjustmentTotals;
      this.approvedFinancialAdjustmentTotals.set({
        expenseBudgetDelta: this.toNumber(approvedTotals?.expenseBudgetDelta),
        customerChargeDelta: this.toNumber(approvedTotals?.customerChargeDelta),
        companyAdvanceAmount: this.toNumber(approvedTotals?.companyAdvanceAmount),
      });
      const expenseAvailability = booking.expenseAvailability;
      this.expenseAvailability.set({
        verifiedTotal: this.toNumber(expenseAvailability?.verifiedTotal),
        approvedCompanyAdvance: this.toNumber(expenseAvailability?.approvedCompanyAdvance),
        expenseTotal: this.toNumber(expenseAvailability?.expenseTotal),
        availableAmount: this.toNumber(expenseAvailability?.availableAmount),
      });
      this.tourMembers.set(
        booking.booking_memberTravelers.map((member) => ({
          id: member.id,
          travelerNo: member.travelerNo,
          fullName: member.name,
          citizenId: member.idcard ?? '',
          phoneNumber: member.phone ?? '',
          passportFileName: member.passportPath ?? '',
          isLeadTraveler: member.isLeadTraveler,
          status: member.status,
        })),
      );
      this.payments.set(booking.booking_payments.map((payment) => this.mapPayment(payment)));
      this.refunds.set(booking.booking_refunds.map((refund) => this.mapRefund(refund)));
      this.expenses.set(booking.expenseRequests.map((expense) => this.mapExpense(expense)));
      this.commissions.set(
        booking.booking_commissions.map((commission) => this.mapCommission(commission)),
      );
      // Booking เดิมต้องใช้ราคาและยอดรวมที่บันทึกไว้ใน Booking
      // ไม่ดึงราคาปัจจุบันของ Tour มาทับ เพราะราคาทัวร์ใหม่มีผลกับ Booking ใหม่เท่านั้น
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('โหลดรายละเอียด Booking ไม่สำเร็จ', message);
      this.isNotFound.set(true);
    } finally {
      this.isLoading.set(false);
      this.overlay.hideLoading();
    }
  }

  private async loadTourFinancialAdjustments(tourId: string | null): Promise<void> {
    if (!tourId || !this.canViewFinancialAdjustments()) {
      this.tourFinancialAdjustments.set([]);
      return;
    }

    try {
      this.tourFinancialAdjustments.set(await this.tourService.getFinancialAdjustments(tourId));
    } catch {
      this.tourFinancialAdjustments.set([]);
    }
  }

  private toNumber(value: number | string | null | undefined): number {
    const parsedValue = Number(value ?? 0);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  private toPaymentDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toPaymentTime(value: Date): string {
    const hour = String(value.getHours()).padStart(2, '0');
    const minute = String(value.getMinutes()).padStart(2, '0');
    return `${hour}:${minute}`;
  }

  adjustmentAmount(value: number | string): number {
    return this.toNumber(value);
  }

  passportUrl(passportPath: string): string {
    return this.activeBookingService().resolveFileUrl(passportPath);
  }

  openFinancialAdjustmentModal(): void {
    if (!this.canCreateFinancialAdjustment()) return;

    this.financialAdjustmentForm.reset();
    this.financialAdjustmentSubmitted.set(false);
    this.advanceAttachmentFile.set(null);
    this.advanceAttachmentFileName.set('');
    this.isFinancialAdjustmentModalOpen.set(true);
  }

  closeFinancialAdjustmentModal(): void {
    this.isFinancialAdjustmentModalOpen.set(false);
    this.financialAdjustmentSubmitted.set(false);
  }

  selectAdvanceAttachment(event: FileSelectEvent): void {
    const file = event.files[0] ?? null;
    this.advanceAttachmentFile.set(file);
    this.advanceAttachmentFileName.set(file?.name ?? '');
  }

  hasCompanyAdvanceRequest(): boolean {
    return this.toNumber(this.financialAdjustmentForm.controls.companyAdvanceAmount.value) > 0;
  }

  hasFinancialAdjustmentAmount(): boolean {
    const value = this.financialAdjustmentForm.getRawValue();
    return (
      this.toNumber(value.expenseBudgetDelta) > 0 ||
      this.toNumber(value.customerChargeDelta) > 0 ||
      this.toNumber(value.companyAdvanceAmount) > 0
    );
  }

  hasExpenseBudgetIncrease(): boolean {
    return this.toNumber(this.financialAdjustmentForm.controls.expenseBudgetDelta.value) > 0;
  }

  expenseBudgetIncreaseLimit(): number {
    return Math.max(this.projectedCustomerChargeTotal(), 0);
  }

  isExpenseBudgetOverLimit(): boolean {
    return (
      this.hasExpenseBudgetIncrease() &&
      this.toNumber(this.financialAdjustmentForm.controls.expenseBudgetDelta.value) >
        this.expenseBudgetIncreaseLimit()
    );
  }

  expenseBudgetMaxInput(): number | undefined {
    return this.hasExpenseBudgetIncrease() ? this.expenseBudgetIncreaseLimit() : undefined;
  }

  projectedExpenseBudget(): number {
    return (
      this.adjustedExpenseBudget() +
      this.toNumber(this.financialAdjustmentForm.controls.expenseBudgetDelta.value)
    );
  }

  projectedCustomerChargeTotal(): number {
    return (
      this.adjustedBookingTotal() +
      this.toNumber(this.financialAdjustmentForm.controls.customerChargeDelta.value)
    );
  }

  projectedCompanyAdvanceTotal(): number {
    return (
      this.approvedCompanyAdvanceAmount() +
      this.toNumber(this.financialAdjustmentForm.controls.companyAdvanceAmount.value)
    );
  }

  isFinancialAdjustmentFieldInvalid(
    field: keyof typeof this.financialAdjustmentForm.controls,
  ): boolean {
    const control = this.financialAdjustmentForm.controls[field];
    return control.invalid && (control.touched || this.financialAdjustmentSubmitted());
  }

  async saveFinancialAdjustment(): Promise<void> {
    if (!this.canCreateFinancialAdjustment()) return;
    this.financialAdjustmentSubmitted.set(true);
    this.financialAdjustmentForm.markAllAsTouched();

    const missingAdvanceAttachment =
      this.hasCompanyAdvanceRequest() && !this.advanceAttachmentFile();

    if (
      this.financialAdjustmentForm.invalid ||
      !this.hasFinancialAdjustmentAmount() ||
      missingAdvanceAttachment ||
      this.isExpenseBudgetOverLimit()
    ) {
      return;
    }

    const value = this.financialAdjustmentForm.getRawValue();
    this.isSavingFinancialAdjustment.set(true);
    try {
      const adjustment = await this.financialAdjustmentService.create(
        this.bookingId,
        {
          expenseBudgetDelta: this.toNumber(value.expenseBudgetDelta),
          customerChargeDelta: this.toNumber(value.customerChargeDelta),
          companyAdvanceAmount: this.toNumber(value.companyAdvanceAmount),
          reason: value.reason!.trim(),
        },
        this.hasCompanyAdvanceRequest() ? this.advanceAttachmentFile() : null,
      );
      this.closeFinancialAdjustmentModal();
      await Promise.all([this.loadBooking(), this.loadFinancialAdjustments()]);
      this.overlay.success(
        'บันทึกคำขอเรียบร้อย',
        `สร้างคำขอ ${adjustment.adjustmentNo} และส่งให้หัวหน้าตรวจสอบแล้ว`,
      );
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('บันทึกคำขอปรับวงเงินไม่สำเร็จ', message);
    } finally {
      this.isSavingFinancialAdjustment.set(false);
    }
  }

  async openCollaboratorModal(): Promise<void> {
    if (!this.canManageBooking()) return;
    this.collaboratorForm.reset({ collaboratorId: this.booking()?.collaboratorId ?? null });
    this.isCollaboratorModalOpen.set(true);
    this.isLoadingSalesStaff.set(true);
    try {
      const staff = await this.bookingCollaboratorService.getActiveSalesStaff();
      this.salesStaffOptions.set(
        staff.map((person) => ({
          value: person.id,
          label: `${person.firstName} ${person.lastName} (${person.employeeCode})`,
        })),
      );
    } catch {
      this.salesStaffOptions.set([]);
      this.overlay.error('โหลดรายชื่อผู้ดูแลไม่สำเร็จ', 'กรุณาลองใหม่อีกครั้ง');
    } finally {
      this.isLoadingSalesStaff.set(false);
    }
  }

  closeCollaboratorModal(): void {
    if (this.isSavingCollaborator()) return;
    this.isCollaboratorModalOpen.set(false);
  }

  async saveCollaborator(): Promise<void> {
    if (!this.canManageBooking()) return;
    if (this.collaboratorForm.invalid) {
      this.collaboratorForm.markAllAsTouched();
      return;
    }
    const collaboratorId = this.collaboratorForm.controls.collaboratorId.value!;
    const wasAssigned = !!this.booking()?.collaboratorId;
    this.isSavingCollaborator.set(true);
    try {
      if (wasAssigned) {
        await this.bookingCollaboratorService.update(this.bookingId, collaboratorId);
      } else {
        await this.bookingCollaboratorService.create(this.bookingId, collaboratorId);
      }
      this.isCollaboratorModalOpen.set(false);
      await this.loadBooking();
      this.overlay.success(wasAssigned ? 'เปลี่ยนผู้ดูแลสำเร็จ' : 'เพิ่มผู้ดูแลสำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('บันทึกผู้ดูแลไม่สำเร็จ', message);
    } finally {
      this.isSavingCollaborator.set(false);
    }
  }

  async deleteCollaborator(): Promise<void> {
    if (!this.canManageBooking() || !this.booking()?.collaboratorId) return;
    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการลบผู้ดูแล',
      message: `ต้องการลบ ${this.booking()?.collaboratorName} ออกจาก Booking นี้หรือไม่`,
      confirmText: 'ลบผู้ดูแล',
    });
    if (!confirmed) return;
    this.isSavingCollaborator.set(true);
    try {
      await this.bookingCollaboratorService.delete(this.bookingId);
      this.isCollaboratorModalOpen.set(false);
      await this.loadBooking();
      this.overlay.success('ลบผู้ดูแลสำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('ลบผู้ดูแลไม่สำเร็จ', message);
    } finally {
      this.isSavingCollaborator.set(false);
    }
  }

  addTourMember(): void {
    if (this.isTourMemberLimitReached()) {
      this.overlay.error(
        'เพิ่มสมาชิกไม่ได้',
        `จำนวนสมาชิกครบ ${this.tourMemberLimit()} คนตามที่ Booking กำหนดแล้ว`,
      );
      return;
    }
    this.editingTourMember.set(null);
    this.memberForm.reset({ isLeadTraveler: false });
    this.passportFile.set(null);
    this.passportFileName.set('');
    this.isMemberModalOpen.set(true);
  }

  closeMemberModal(): void {
    this.isMemberModalOpen.set(false);
    this.editingTourMember.set(null);
  }

  editTourMember(member: TourMember): void {
    this.editingTourMember.set(member);
    this.memberForm.reset({
      fullName: member.fullName,
      citizenId: member.citizenId,
      phoneNumber: member.phoneNumber,
      isLeadTraveler: member.isLeadTraveler,
    });
    this.passportFileName.set(member.passportFileName);
    this.passportFile.set(null);
    this.isMemberModalOpen.set(true);
  }

  selectPassportFile(event: FileSelectEvent): void {
    const file = event.files[0] ?? null;
    if (file && file.size > this.maxPassportFileSize()) {
      this.passportFile.set(null);
      this.passportFileName.set('');
      this.overlay.error('เลือกไฟล์ไม่ได้', `ไฟล์แนบต้องมีขนาดไม่เกิน ${this.maxPassportFileSizeLabel()}`);
      return;
    }
    this.passportFile.set(file);
    this.passportFileName.set(file?.name ?? '');
  }

  isMemberFieldInvalid(field: keyof typeof this.memberForm.controls): boolean {
    const control = this.memberForm.controls[field];
    return control.touched && control.invalid;
  }

  async saveTourMember(): Promise<void> {
    if (this.memberForm.invalid || !this.passportFileName()) {
      this.memberForm.markAllAsTouched();
      return;
    }

    const value = this.memberForm.getRawValue();
    const input = {
      name: value.fullName!,
      idcard: value.citizenId!,
      phone: value.phoneNumber!,
      isLeadTraveler: value.isLeadTraveler ?? false,
    };
    const editingMember = this.editingTourMember();
    this.isSavingMember.set(true);
    try {
      if (editingMember) {
        await this.activeBookingService().updateTraveler(
          this.bookingId,
          editingMember.id,
          input,
          this.passportFile(),
        );
      } else {
        await this.activeBookingService().createTraveler(
          this.bookingId,
          input,
          this.passportFile()!,
        );
      }
      this.closeMemberModal();
      await this.loadBooking();
      this.overlay.success(editingMember ? 'แก้ไขสมาชิกสำเร็จ' : 'เพิ่มสมาชิกสำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถบันทึกสมาชิกทัวร์ได้';
      this.overlay.error('บันทึกสมาชิกไม่สำเร็จ', message);
    } finally {
      this.isSavingMember.set(false);
    }
  }

  async deleteTourMember(member: TourMember): Promise<void> {
    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการลบสมาชิกทัวร์',
      message: `ต้องการลบ ${member.fullName} หรือไม่`,
      confirmText: 'ลบสมาชิก',
    });
    if (!confirmed) return;
    try {
      await this.activeBookingService().deleteTraveler(this.bookingId, member.id);
      await this.loadBooking();
      this.overlay.success('ลบสมาชิกสำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถลบสมาชิกทัวร์ได้';
      this.overlay.error('ลบสมาชิกไม่สำเร็จ', message);
    }
  }

  openPaymentModal(): void {
    if (this.remainingBalance() <= 0) return;

    const now = new Date();
    this.editingPayment.set(null);
    this.paymentForm.reset({
      hasWithholdingTax: false,
      withholdingTaxAmount: 0,
      paidDate: new Date(now),
      paidTime: new Date(now),
    });
    this.paymentSlipFile.set(null);
    this.paymentSlipFileName.set('');
    this.setPaymentAmountValidator();
    this.isPaymentModalOpen.set(true);
  }

  openRefundModal(): void {
    if (!this.canCreateRefund()) return;
    this.refundForm.reset();
    this.setRefundAmountValidator();
    this.refundAttachmentFile.set(null);
    this.refundAttachmentFileName.set('');
    this.isRefundModalOpen.set(true);
  }

  refundButtonTitle(): string {
    if (this.isBookingDeleted()) return 'Booking นี้ถูกลบแล้ว';
    if (this.booking()?.status === 'REFUND_PENDING') return 'Booking อยู่ระหว่างคืนเงิน';
    if (this.booking()?.status === 'REFUND') return 'Booking คืนเงินเสร็จสิ้นแล้ว';
    if (this.isBookingCompleted()) return 'Booking ปิดเคสแล้ว';
    if (this.verifiedCustomerPaidTotal() <= 0) {
      return 'ต้องมีรายการชำระเงินที่ตรวจสอบแล้วอย่างน้อย 1 รายการ';
    }
    if (this.hasPendingRefund()) {
      return 'มีรายการคืนเงินที่กำลังดำเนินการอยู่ กรุณาดำเนินการรายการเดิมให้เสร็จก่อน';
    }
    if (this.remainingFullRefundAmount() <= 0) {
      return 'ยอดคืนเงินที่กำลังดำเนินการครบตามยอดชำระแล้ว';
    }
    return 'เพิ่มรายการคืนเงิน';
  }

  closeRefundModal(): void {
    if (this.isSavingRefund()) return;
    this.isRefundModalOpen.set(false);
    this.refundForm.reset();
    this.refundAttachmentFile.set(null);
    this.refundAttachmentFileName.set('');
  }

  isRefundFieldInvalid(field: keyof typeof this.refundForm.controls): boolean {
    const control = this.refundForm.controls[field];
    return control.touched && control.invalid;
  }

  selectRefundAttachment(event: FileSelectEvent): void {
    const file = event.files[0] ?? null;
    this.refundAttachmentFile.set(file);
    this.refundAttachmentFileName.set(file?.name ?? '');
  }

  clearRefundAttachment(): void {
    this.refundAttachmentFile.set(null);
    this.refundAttachmentFileName.set('');
  }

  refundTypeLabel(type: BookingRefundType): string {
    return this.refundTypeOptions.find((option) => option.value === type)?.label ?? type;
  }

  onRefundTypeChange(type: BookingRefundType | null): void {
    this.refundForm.controls.amount.setValue(
      type === 'FULL' ? this.remainingFullRefundAmount() : null,
    );
    this.setRefundAmountValidator();
    this.refundForm.controls.amount.markAsUntouched();
    this.refundForm.controls.amount.updateValueAndValidity();
  }

  checkRefundAmount(value: number | string | null | undefined): void {
    const amount = Number(value ?? 0);
    const control = this.refundForm.controls.amount;
    control.updateValueAndValidity({ emitEvent: false });
    if (Number.isFinite(amount) && amount > this.refundAmountLimit()) {
      control.markAsTouched();
    }
  }

  refundAmountLimit(): number {
    return this.refundForm.controls.refundType.value === 'FULL'
      ? this.remainingFullRefundAmount()
      : this.availableRefundAmount();
  }

  private setRefundAmountValidator(): void {
    this.refundForm.controls.amount.setValidators([
      Validators.required,
      Validators.min(1),
      Validators.max(this.refundAmountLimit()),
    ]);
    this.refundForm.controls.amount.updateValueAndValidity({ emitEvent: false });
  }

  async saveRefund(): Promise<void> {
    if (this.refundForm.invalid || !this.refundAttachmentFile() || this.isSavingRefund()) {
      this.refundForm.markAllAsTouched();
      return;
    }

    const value = this.refundForm.getRawValue();
    if ((value.amount ?? 0) > this.refundAmountLimit()) {
      this.refundForm.controls.amount.markAsTouched();
      return;
    }
    this.isSavingRefund.set(true);
    try {
      await this.activeBookingService().createRefund(
        this.bookingId,
        {
          refundName: value.refundName!.trim(),
          bankName: value.bankName!,
          accountNumber: value.accountNumber!.trim(),
          refundType: value.refundType!,
          amount: value.amount!,
          reason: value.reason!.trim(),
        },
        this.refundAttachmentFile()!,
      );
      this.isSavingRefund.set(false);
      this.closeRefundModal();
      await this.loadBooking();
      this.overlay.success('เพิ่มรายการคืนเงินสำเร็จ', 'รายการถูกบันทึกเป็นฉบับร่างแล้ว');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถบันทึกรายการคืนเงินได้';
      this.overlay.error('บันทึกรายการคืนเงินไม่สำเร็จ', message);
    } finally {
      this.isSavingRefund.set(false);
    }
  }

  async deleteRefund(refund: BookingRefund): Promise<void> {
    if (refund.status !== 'REJECTED' || !this.canDeleteRejectedBookingRecord()) return;
    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการลบรายการคืนเงิน',
      message: `ต้องการลบ ${refund.refundNo} หรือไม่`,
      confirmText: 'ลบรายการ',
    });
    if (!confirmed) return;
    try {
      await this.activeBookingService().deleteRefund(this.bookingId, refund.id);
      await this.loadBooking();
      this.overlay.success('ลบรายการคืนเงินสำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถลบรายการคืนเงินได้';
      this.overlay.error('ลบรายการคืนเงินไม่สำเร็จ', message);
    }
  }

  closePaymentModal(): void {
    this.isPaymentModalOpen.set(false);
    this.editingPayment.set(null);
  }

  editPayment(payment: BookingPayment): void {
    if (['WAITING_VERIFY', 'VERIFIED', 'REJECTED'].includes(payment.status)) return;
    this.editingPayment.set(payment);
    this.paymentForm.reset({
      payerName: payment.payerName,
      paymentStage: payment.paymentStage,
      paymentType: payment.paymentMethod,
      creditCardFeePercent: payment.creditCardFeePercent,
      creditCardFeeResponsibility: payment.creditCardFeeResponsibility,
      amount: payment.customerPaidAmount,
      hasWithholdingTax: payment.withholdingTaxAmount > 0,
      withholdingTaxAmount: payment.withholdingTaxAmount,
      paidDate: new Date(payment.paidAt),
      paidTime: new Date(payment.paidAt),
    });
    this.paymentSlipFile.set(null);
    this.paymentSlipFileName.set(payment.slipFileName);
    this.setPaymentAmountValidator();
    this.isPaymentModalOpen.set(true);
  }

  checkPaymentAmount(value: number | string | null | undefined): void {
    const amount = Number(value ?? 0);
    const limit = this.paymentAmountLimit();
    if (!Number.isFinite(amount) || amount <= limit) return;
    this.paymentForm.controls.amount.markAsTouched();
    this.paymentForm.controls.amount.updateValueAndValidity({ emitEvent: false });
  }

  onWithholdingTaxChange(checked: boolean): void {
    if (!checked) {
      this.paymentForm.controls.withholdingTaxAmount.setValue(0);
    }
  }

  private setPaymentAmountValidator(): void {
    this.paymentForm.controls.amount.setValidators([
      Validators.required,
      Validators.min(1),
      Validators.max(this.paymentAmountLimit()),
    ]);
    this.paymentForm.controls.amount.updateValueAndValidity();
  }

  selectPaymentSlip(event: FileSelectEvent): void {
    const file = event.files[0] ?? null;
    this.paymentSlipFile.set(file);
    this.paymentSlipFileName.set(file?.name ?? '');
  }

  isPaymentFieldInvalid(field: keyof typeof this.paymentForm.controls): boolean {
    const control = this.paymentForm.controls[field];
    return control.touched && control.invalid;
  }

  async savePayment(): Promise<void> {
    if (this.paymentForm.controls.paymentType.value === 'CREDIT_CARD') {
      const feePercentControl = this.paymentForm.controls.creditCardFeePercent;
      const feeResponsibilityControl = this.paymentForm.controls.creditCardFeeResponsibility;
      if (feePercentControl.value === null || feeResponsibilityControl.value === null) {
        feePercentControl.markAsTouched();
        feeResponsibilityControl.markAsTouched();
        return;
      }
    }

    if (this.paymentForm.invalid || !this.paymentSlipFileName()) {
      this.paymentForm.markAllAsTouched();
      return;
    }

    const value = this.paymentForm.getRawValue();
    if (!value.paidDate || !value.paidTime) return;
    if ((value.amount ?? 0) > this.paymentAmountLimit()) {
      this.checkPaymentAmount(value.amount);
      return;
    }
    const input = {
      payerName: value.payerName!,
      paymentStage: value.paymentStage!,
      paymentMethod: value.paymentType!,
      creditCardFeePercent:
        value.paymentType === 'CREDIT_CARD' ? (value.creditCardFeePercent ?? 0) : 0,
      creditCardFeeResponsibility:
        value.paymentType === 'CREDIT_CARD' ? value.creditCardFeeResponsibility : null,
      amount: value.amount!,
      withholdingTaxAmount: value.hasWithholdingTax ? (value.withholdingTaxAmount ?? 0) : 0,
      paidDate: this.toPaymentDate(value.paidDate),
      paymentTime: this.toPaymentTime(value.paidTime),
    };
    const editing = this.editingPayment();
    this.isSavingPayment.set(true);
    try {
      if (editing) {
        await this.activeBookingService().updatePayment(
          this.bookingId,
          editing.id,
          input,
          this.paymentSlipFile(),
        );
      } else {
        await this.activeBookingService().createPayment(
          this.bookingId,
          input,
          this.paymentSlipFile()!,
        );
      }
      this.closePaymentModal();
      await this.loadBooking();
      this.overlay.success(editing ? 'แก้ไขการชำระเงินสำเร็จ' : 'บันทึกการชำระเงินสำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถบันทึกการชำระเงินได้';
      this.overlay.error('บันทึกการชำระเงินไม่สำเร็จ', message);
    } finally {
      this.isSavingPayment.set(false);
    }
  }

  async deletePayment(payment: BookingPayment): Promise<void> {
    if (['WAITING_VERIFY', 'VERIFIED'].includes(payment.status)) return;
    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการลบรายการชำระเงิน',
      message: `ต้องการลบ ${payment.receiptNumber} หรือไม่`,
      confirmText: 'ลบรายการ',
    });
    if (!confirmed) return;
    try {
      await this.activeBookingService().deletePayment(this.bookingId, payment.id);
      await this.loadBooking();
      this.overlay.success('ลบรายการชำระเงินสำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถลบการชำระเงินได้';
      this.overlay.error('ลบการชำระเงินไม่สำเร็จ', message);
    }
  }

  paymentMethodLabel(method: PaymentMethod): string {
    return this.paymentTypeOptions.find((option) => option.value === method)?.label ?? method;
  }

  paymentStageLabel(stage: PaymentStage): string {
    return this.paymentStageOptions.find((option) => option.value === stage)?.label ?? stage;
  }

  private mapPayment(payment: CustomerPaymentItem): BookingPayment {
    return {
      id: payment.id,
      receiptNumber: payment.paymentNo,
      paidAt: new Date(
        `${payment.paymentDate.slice(0, 10)}T${new Date(payment.paymentTime).toISOString().slice(11, 19)}`,
      ),
      payerName: payment.payerName,
      paymentStage: payment.paymentStage,
      paymentMethod: payment.paymentMethod,
      amount: this.toNumber(payment.amount),
      creditCardFeePercent: payment.creditCardFeePercent,
      creditCardFeeResponsibility: payment.creditCardFeeResponsibility,
      creditCardFeeAmount: this.toNumber(payment.creditCardFeeAmount),
      customerPaidAmount: this.toNumber(payment.customerPaidAmount ?? payment.amount),
      withholdingTaxAmount: this.toNumber(payment.withholdingTaxAmount),
      slipFileName: payment.slipPath ?? '',
      receiptUrl: payment.receiptUrl ?? '',
      quotationUrl: payment.quotationUrl ?? '',
      status: payment.status,
    };
  }

  private mapRefund(refund: BookingRefundItem): BookingRefund {
    const payment = refund.paymentTransaction?.status === 'PAID' ? refund.paymentTransaction : null;
    return {
      id: refund.id,
      refundNo: refund.refundNo,
      refundName: refund.namerefund,
      bankName: refund.bankName ?? '-',
      accountNumber: refund.account_number,
      refundType: refund.refundType,
      amount: this.toNumber(refund.amount),
      reason: refund.reason,
      attachmentPath: refund.attachmentPath ?? '',
      transferSlipUrl: payment?.receiptUrl ?? '',
      transferredAt: payment?.transferredAt ? new Date(payment.transferredAt) : payment ? new Date(payment.paymentDate) : null,
      status: refund.status,
      paidAmount:
        payment
          ? this.toNumber(payment.amount)
          : 0,
      createdAt: new Date(refund.createdAt),
    };
  }

  refundFileUrl(path: string): string {
    return this.bookingService.resolveFileUrl(path);
  }

  openExpenseModal(): void {
    if (!this.canCreateExpense()) return;
    this.editingExpense.set(null);
    const joinTour = this.booking()?.bookingType === 'JOIN_TOUR';
    this.expenseForm.reset({
      expenseNameSnapshot: joinTour ? 'ค่าใช้จ่ายโฮลเซล' : '',
      wholesaleId: joinTour ? (this.booking()?.wholesaleId ?? '') : '',
    });
    this.setWholesaleValidator(joinTour);
    this.setExpenseAmountValidator();
    this.purchaseOrderFileName.set('');
    this.purchaseOrderFile.set(null);
    this.isExpenseModalOpen.set(true);
  }

  editExpense(expense: BookingExpense): void {
    if (expense.status !== 'DRAFT') return;
    this.editingExpense.set(expense);
    const joinTour = this.booking()?.bookingType === 'JOIN_TOUR';
    this.setWholesaleValidator(joinTour || Boolean(expense.wholesaleId));
    this.expenseForm.reset({
      description: expense.description,
      expenseNameSnapshot: joinTour ? 'ค่าใช้จ่ายโฮลเซล' : expense.expenseNameSnapshot,
      wholesaleId: joinTour
        ? (expense.wholesaleId ?? this.booking()?.wholesaleId ?? '')
        : (expense.wholesaleId ?? ''),
      amount: expense.amount,
      actualTransferAmount: expense.actualTransferAmount,
    });
    this.setExpenseAmountValidator();
    this.purchaseOrderFile.set(null);
    this.purchaseOrderFileName.set(expense.purchaseOrderFileName);
    this.isExpenseModalOpen.set(true);
  }

  checkExpenseAmount(value: number | string | null | undefined): void {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount) || amount <= this.expenseActualTransferLimit()) return;
    this.expenseForm.controls.actualTransferAmount.markAsTouched();
    this.expenseForm.controls.actualTransferAmount.updateValueAndValidity({ emitEvent: false });
  }

  private setExpenseAmountValidator(): void {
    this.expenseForm.controls.actualTransferAmount.setValidators([
      Validators.required,
      Validators.min(1),
      Validators.max(this.expenseActualTransferLimit()),
    ]);
    this.expenseForm.controls.actualTransferAmount.updateValueAndValidity();
  }

  private setWholesaleValidator(required: boolean): void {
    const control = this.expenseForm.controls.wholesaleId;
    control.setValidators(required ? [Validators.required] : []);
    control.updateValueAndValidity({ emitEvent: false });
  }

  closeExpenseModal(): void {
    this.isExpenseModalOpen.set(false);
    this.editingExpense.set(null);
  }

  editableExpenseAmount(): number {
    const editingExpense = this.editingExpense();
    return (
      this.availableExpenseAmount() +
      (editingExpense ? this.expenseFundingAmount(editingExpense) : 0)
    );
  }

  editableExpenseBudget(): number {
    const editingExpense = this.editingExpense();
    return (
      this.expenseBudgetRemaining() +
      (editingExpense ? this.expenseEffectiveAmount(editingExpense) : 0)
    );
  }

  expenseActualTransferLimit(): number {
    const editableLimit = Math.min(this.editableExpenseAmount(), this.editableExpenseBudget());
    const editingExpense = this.editingExpense();
    if (editingExpense?.source !== 'BOOKING_ESTIMATE') return editableLimit;
    return Math.min(editableLimit, editingExpense.amount ?? 0);
  }

  expenseEffectiveAmount(expense: BookingExpense): number {
    return expense.actualTransferAmount ?? expense.amount ?? 0;
  }

  expenseFundingAmount(expense: BookingExpense): number {
    return expense.actualTransferAmount ?? 0;
  }

  selectPurchaseOrder(event: FileSelectEvent): void {
    const file = event.files[0] ?? null;
    this.purchaseOrderFile.set(file);
    this.purchaseOrderFileName.set(file?.name ?? '');
  }

  isExpenseFieldInvalid(field: keyof typeof this.expenseForm.controls): boolean {
    const control = this.expenseForm.controls[field];
    return control.touched && control.invalid;
  }

  async saveExpense(submitForReview = false): Promise<void> {
    const editing = this.editingExpense();
    if (!editing && !this.canCreateExpense()) {
      this.closeExpenseModal();
      this.overlay.error(
        'สร้างใบขอเบิกไม่ได้',
        this.hasBlockingCommissionForExpense()
          ? this.booking()?.bookingType === 'JOIN_TOUR'
            ? 'รายการจองนี้มีค่าคอมมิชชั่นที่ตรวจสอบแล้ว'
            : 'รายการจองนี้มีค่าคอมมิชชั่นแล้ว'
          : 'ยังไม่มียอดชำระหรือวงเงินสำรองที่อนุมัติ หรือวงเงินเบิกคงเหลือไม่เพียงพอ',
      );
      return;
    }
    if (this.expenseForm.invalid || !this.purchaseOrderFileName()) {
      this.expenseForm.markAllAsTouched();
      return;
    }
    const value = this.expenseForm.getRawValue();
    if (submitForReview && (value.actualTransferAmount ?? 0) > this.expenseActualTransferLimit()) {
      this.overlay.error(
        'ยังส่งใบขอเบิกไม่ได้',
        `ยอดที่ต้องโอนจริงต้องไม่เกิน ${this.expenseActualTransferLimit().toFixed(2)} บาท`,
      );
      return;
    }
    if (
      this.expenseActualTransferLimit() <= 0 ||
      (value.actualTransferAmount ?? 0) > this.expenseActualTransferLimit()
    ) {
      this.checkExpenseAmount(value.actualTransferAmount);
      return;
    }
    this.isSavingExpense.set(true);
    try {
      const input = {
        expenseNameSnapshot: value.expenseNameSnapshot!,
        ...(value.wholesaleId ? { wholesaleId: value.wholesaleId } : {}),
        description: value.description!,
        actualTransferAmount: value.actualTransferAmount!,
        submitForReview,
      };
      if (editing) {
        await this.activeBookingService().updateExpense(
          this.bookingId,
          editing.id,
          input,
          this.purchaseOrderFile(),
        );
      } else {
        await this.activeBookingService().createExpense(
          this.bookingId,
          input,
          this.purchaseOrderFile()!,
        );
      }
      this.closeExpenseModal();
      await this.loadBooking();
      this.overlay.success(
        submitForReview ? 'ส่งใบขอเบิกสำเร็จ' : 'บันทึกร่างสำเร็จ',
        submitForReview
          ? 'ส่งรายการให้หัวหน้าอนุมัติแล้ว'
          : editing
            ? 'อัปเดตรายการร่างแล้ว'
            : 'เพิ่มรายการร่างใน Booking แล้ว',
      );
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถสร้างใบขอเบิกได้';
      this.overlay.error('สร้างใบขอเบิกไม่สำเร็จ', message);
    } finally {
      this.isSavingExpense.set(false);
    }
  }

  private mapExpense(expense: ExpenseRequestItem): BookingExpense {
    return {
      id: expense.id,
      code: expense.expenseNo,
      description: expense.description,
      expenseNameSnapshot: expense.expenseNameSnapshot,
      wholesaleId: expense.wholesaleId,
      wholesaleNameSnapshot: expense.wholesaleNameSnapshot ?? expense.wholesale?.name ?? '',
      requester: `${expense.requestedBy.firstName} ${expense.requestedBy.lastName}`.trim(),
      amount: expense.amount === null ? null : this.toNumber(expense.amount),
      actualTransferAmount:
        expense.actualTransferAmount === null ? null : this.toNumber(expense.actualTransferAmount),
      source: expense.source,
      purchaseOrderFileName: expense.attachmentPath ?? '',
      transferSlipUrl: expense.receiptUrl ?? '',
      receiptFileName: expense.receiptPath ?? '',
      receiptSubmittedAt: expense.receiptSubmittedAt ? new Date(expense.receiptSubmittedAt) : null,
      status: expense.status,
      createdAt: new Date(expense.createdAt),
      paidAmount: expense.paymentTransactions
        .filter((transaction) => transaction.status === 'PAID')
        .reduce((total, transaction) => total + this.toNumber(transaction.amount), 0),
    };
  }

  async deleteExpense(expense: BookingExpense): Promise<void> {
    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการลบใบขอเบิก',
      message: `ต้องการลบ ${expense.code} หรือไม่`,
      confirmText: 'ลบรายการ',
    });
    if (!confirmed) return;
    this.overlay.showLoading('กำลังลบใบขอเบิก...');
    try {
      await this.activeBookingService().deleteExpense(this.bookingId, expense.id);
      await this.loadBooking();
      this.overlay.success('ลบใบขอเบิกสำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถลบใบขอเบิกได้';
      this.overlay.error('ลบใบขอเบิกไม่สำเร็จ', message);
    }
  }

  openExpenseReceiptModal(expense: BookingExpense): void {
    this.selectedReceiptExpense.set(expense);
    this.expenseReceiptFile.set(null);
    this.expenseReceiptFileName.set('');
  }

  closeExpenseReceiptModal(): void {
    if (this.isSavingExpenseReceipt()) return;
    this.selectedReceiptExpense.set(null);
    this.expenseReceiptFile.set(null);
    this.expenseReceiptFileName.set('');
  }

  selectExpenseReceipt(event: FileSelectEvent): void {
    const file = event.files[0] ?? null;
    this.expenseReceiptFile.set(file);
    this.expenseReceiptFileName.set(file?.name ?? '');
  }

  async saveExpenseReceipt(): Promise<void> {
    const expense = this.selectedReceiptExpense();
    const receipt = this.expenseReceiptFile();
    if (!expense || !receipt) return;
    this.isSavingExpenseReceipt.set(true);
    try {
      await this.activeBookingService().submitExpenseReceipt(this.bookingId, expense.id, receipt);
      this.selectedReceiptExpense.set(null);
      this.expenseReceiptFile.set(null);
      this.expenseReceiptFileName.set('');
      await this.loadBooking();
      this.overlay.success('ส่งใบเสร็จสำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถส่งใบเสร็จได้';
      this.overlay.error('ส่งใบเสร็จไม่สำเร็จ', message);
    } finally {
      this.isSavingExpenseReceipt.set(false);
    }
  }

  canCreateCommissionFor(recipientRole: BookingCommissionItem['recipientRole']): boolean {
    const booking = this.booking();
    const hasActiveCommissionForRecipient = this.commissions().some(
      (commission) =>
        commission.recipientRole === recipientRole &&
        !['REJECTED', 'CANCELLED', 'DELETED'].includes(commission.status),
    );
    const rate = recipientRole === 'SALE'
      ? (booking?.saleCommission ?? 0) + (booking?.specialCommission ?? 0)
      : 50;
    const hasRequiredRecipient = recipientRole !== 'CARETAKER' || Boolean(booking?.collaboratorId);
    const isCaretakerCaseClosed = recipientRole !== 'CARETAKER' || booking?.status === 'COMPLETED';
    const isSaleBookingConfirmed = this.isSaleCommissionBookingConfirmed(recipientRole);
    return (
      this.canViewCommissionButton(recipientRole) &&
      !this.isBookingDeleted() &&
      this.isCommissionPaymentComplete() &&
      hasRequiredRecipient &&
      isCaretakerCaseClosed &&
      isSaleBookingConfirmed &&
      rate > 0 &&
      !hasActiveCommissionForRecipient
    );
  }

  hasCommissionRecipient(recipientRole: BookingCommissionItem['recipientRole']): boolean {
    const booking = this.booking();
    return recipientRole === 'SALE' ? Boolean(booking?.saleName) : Boolean(booking?.collaboratorId);
  }

  canViewCommissionButton(recipientRole: BookingCommissionItem['recipientRole']): boolean {
    const user = this.auth.user();
    const booking = this.booking();
    if (!this.canRequestCommission() || !user || !booking) return false;
    if (user.role.level === ROLE_LEVEL.ADMIN) return true;
    return recipientRole === 'SALE'
      ? booking.saleId === user.id
      : booking.collaboratorId === user.id;
  }

  commissionButtonTitle(recipientRole: BookingCommissionItem['recipientRole']): string {
    if (this.isSalesCreatorRequestingSaleCommission(recipientRole) && this.booking()?.status !== 'COMPLETED') {
      return 'ผู้สร้าง Booking ฝ่ายการขายต้องปิดเคสก่อนเบิกค่าคอมพนักงานขาย';
    }
    if (
      recipientRole === 'SALE' &&
      !['CONFIRMED', 'COMPLETED'].includes(this.booking()?.status ?? '')
    ) {
      return 'ต้องยืนยันการจองก่อนเบิกค่าคอมพนักงานขาย';
    }
    if (!this.isCommissionPaymentComplete()) return 'ยอดชำระที่ตรวจสอบแล้วต้องครบยอด Booking ก่อน';
    if (recipientRole === 'CARETAKER' && this.booking()?.status !== 'COMPLETED') {
      return 'เบิกค่าคอมผู้ดูแลได้หลังปิดเคส Booking เท่านั้น';
    }
    if (recipientRole === 'CARETAKER' && !this.booking()?.collaboratorId) return 'ยังไม่ได้กำหนดผู้ดูแลรายการ';
    if (this.commissions().some((commission) => commission.recipientRole === recipientRole && !['REJECTED', 'CANCELLED', 'DELETED'].includes(commission.status))) {
      return 'มีรายการเบิกของผู้รับสิทธิ์นี้อยู่แล้ว';
    }
    return recipientRole === 'SALE' ? 'เบิกค่าคอมพนักงานขาย' : 'เบิกค่าคอมผู้ดูแล';
  }

  isSaleCommissionBookingConfirmed(
    recipientRole: BookingCommissionItem['recipientRole'],
  ): boolean {
    if (recipientRole !== 'SALE') return true;
    return this.isSalesCreatorRequestingSaleCommission(recipientRole)
      ? this.booking()?.status === 'COMPLETED'
      : ['CONFIRMED', 'COMPLETED'].includes(this.booking()?.status ?? '');
  }

  private isSalesCreatorRequestingSaleCommission(
    recipientRole: BookingCommissionItem['recipientRole'],
  ): boolean {
    const user = this.auth.user();
    const booking = this.booking();
    return Boolean(
      recipientRole === 'SALE' &&
        user?.department.code === DEPARTMENT.SALES &&
        user.role.level !== ROLE_LEVEL.ADMIN &&
        user.role.level !== ROLE_LEVEL.EMPLOYEE_ASSISTANT &&
        booking?.createdById === user.id,
    );
  }

  openCommissionModal(recipientRole: BookingCommissionItem['recipientRole']): void {
    if (!this.canCreateCommissionFor(recipientRole)) return;
    const booking = this.booking();
    this.editingCommission.set(null);
    this.commissionRecipientRole.set(recipientRole);
    this.commissionForm.reset({
      saleCommission: recipientRole === 'SALE' ? booking?.saleCommission ?? 0 : 0,
      specialCommission: recipientRole === 'SALE' ? booking?.specialCommission ?? 0 : 50,
    });
    this.isCommissionModalOpen.set(true);
  }

  closeCommissionModal(): void {
    this.isCommissionModalOpen.set(false);
    this.editingCommission.set(null);
    this.commissionRecipientRole.set('SALE');
  }

  editCommission(commission: BookingCommission): void {
    if (
      !this.isCommissionPaymentComplete() ||
      !this.isSaleCommissionBookingConfirmed(commission.recipientRole)
    ) return;
    this.editingCommission.set(commission);
    this.commissionRecipientRole.set(commission.recipientRole);
    this.commissionForm.reset({
      saleCommission:
        commission.recipientRole === 'SALE'
          ? commission.saleCommission
          : commission.saleCommission / Math.max(commission.pax, 1),
      specialCommission:
        commission.recipientRole === 'SALE'
          ? commission.specialCommission
          : commission.specialCommission / Math.max(commission.pax, 1),
    });
    this.isCommissionModalOpen.set(true);
  }

  isCommissionFieldInvalid(field: keyof typeof this.commissionForm.controls): boolean {
    const control = this.commissionForm.controls[field];
    return control.touched && control.invalid;
  }

  async saveCommission(): Promise<void> {
    const submitForReview = true;
    const recipientRole = this.commissionRecipientRole();
    if (
      !this.isCommissionPaymentComplete() ||
      !this.isSaleCommissionBookingConfirmed(recipientRole) ||
      (!this.editingCommission() && !this.canCreateCommissionFor(recipientRole))
    ) {
      const eligibilityMessage = this.commissionButtonTitle(recipientRole);
      this.closeCommissionModal();
      this.overlay.error(
        'ยังสร้างค่าคอมมิชชั่นไม่ได้',
        eligibilityMessage,
      );
      return;
    }
    if (this.commissionForm.invalid) {
      this.commissionForm.markAllAsTouched();
      return;
    }
    const value = this.commissionForm.getRawValue();
    const saleCommission = value.saleCommission ?? 0;
    const specialCommission = value.specialCommission ?? 0;
    const total =
      recipientRole === 'SALE'
        ? saleCommission + specialCommission
        : (saleCommission + specialCommission) * Math.max(this.booking()?.travelerCount ?? 1, 1);
    if (total > this.availableCommissionAmount()) {
      this.overlay.error(
        'ไม่สามารถบันทึกค่าคอมมิชชั่นได้',
        `ยอดเบิกทั้งหมดรวมค่าคอมมิชชั่นเกินยอดชำระที่ตรวจสอบแล้ว บันทึกค่าคอมมิชชั่นได้ไม่เกิน ${this.availableCommissionAmount().toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท`,
      );
      return;
    }
    const editing = this.editingCommission();
    this.isSavingCommission.set(true);
    try {
      if (editing) {
        await this.activeBookingService().updateCommission(
          this.bookingId,
          editing.id,
          { recipientRole: this.commissionRecipientRole(), saleCommission, specialCommission, submitForReview },
        );
      } else {
        await this.activeBookingService().createCommission(
          this.bookingId,
          { recipientRole: this.commissionRecipientRole(), saleCommission, specialCommission, submitForReview },
        );
      }
      this.closeCommissionModal();
      await this.loadBooking();
      this.overlay.success('ส่งเบิกค่าคอมมิชชั่นสำเร็จ', 'ส่งรายการให้หัวหน้าอนุมัติแล้ว');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถส่งเบิกค่าคอมมิชชั่นได้';
      this.overlay.error('ส่งเบิกค่าคอมมิชชั่นไม่สำเร็จ', message);
    } finally {
      this.isSavingCommission.set(false);
    }
  }

  async deleteCommission(commission: BookingCommission): Promise<void> {
    if (commission.status !== 'REJECTED' || !this.canDeleteRejectedBookingRecord()) return;
    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการลบค่าคอมมิชชั่น',
      message: `ต้องการลบ ${commission.code} หรือไม่`,
      confirmText: 'ลบรายการ',
    });
    if (!confirmed) return;
    try {
      await this.activeBookingService().deleteCommission(this.bookingId, commission.id);
      await this.loadBooking();
      this.overlay.success('ลบค่าคอมมิชชั่นสำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถลบค่าคอมมิชชั่นได้';
      this.overlay.error('ลบค่าคอมมิชชั่นไม่สำเร็จ', message);
    }
  }

  openCommissionRecoveryHistory(commission: BookingCommission): void {
    if (commission.recoveries.length === 0) return;
    this.selectedCommissionRecovery.set(commission);
  }

  closeCommissionRecoveryHistory(): void {
    this.selectedCommissionRecovery.set(null);
  }

  commissionRecoveryTotal(commission: BookingCommission, field: 'recoveryAmount' | 'settledAmount' | 'remainingAmount'): number {
    return commission.recoveries.reduce((total, recovery) => total + recovery[field], 0);
  }

  commissionRecoveryStatusLabel(status: CommissionRecovery['status']): string {
    return {
      PENDING: 'รอหักคืน',
      PARTIAL: 'หักคืนบางส่วน',
      SETTLED: 'หักคืนครบแล้ว',
      WAIVED: 'ยกเว้นการหักคืน',
      CANCELLED: 'ยกเลิกรายการ',
    }[status];
  }

  commissionRecoveryStatusClass(status: CommissionRecovery['status']): string {
    return {
      PENDING: 'bg-red-50 text-red-700 ring-red-200',
      PARTIAL: 'bg-amber-50 text-amber-700 ring-amber-200',
      SETTLED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
      WAIVED: 'bg-slate-100 text-slate-600 ring-slate-200',
      CANCELLED: 'bg-slate-100 text-slate-500 ring-slate-200',
    }[status];
  }

  commissionRecoveryApplicationStatusLabel(
    status: CommissionRecovery['applications'][number]['status'],
  ): string {
    return { RESERVED: 'กันยอดไว้', APPLIED: 'หักคืนแล้ว', RELEASED: 'คืนยอดแล้ว' }[status];
  }

  private mapCommission(commission: BookingCommissionItem): BookingCommission {
    return {
      id: commission.id,
      code: commission.commissionNo,
      recipientRole: commission.recipientRole,
      recipientName: `${commission.sale.firstName} ${commission.sale.lastName}`.trim(),
      requesterName: commission.requestedBy
        ? `${commission.requestedBy.firstName} ${commission.requestedBy.lastName}`.trim()
        : '-',
      pax: commission.pax,
      saleCommissionRate: this.toNumber(commission.saleCommissionRate),
      specialCommissionRate: this.toNumber(commission.specialCommissionRate),
      saleCommission: this.toNumber(commission.saleCommissionAmount),
      specialCommission: this.toNumber(commission.specialCommissionAmount),
      total: this.toNumber(commission.totalAmount),
      attachmentFileName: commission.attachmentPath ?? '',
      status: commission.status,
      createdAt: new Date(commission.createdAt),
      submittedAt: commission.submittedAt ? new Date(commission.submittedAt) : null,
      paidAmount:
        commission.paymentTransaction?.status === 'PAID'
          ? this.toNumber(commission.paymentTransaction.amount)
          : 0,
      recoveries: commission.recoverySources.map((recovery) => ({
        id: recovery.id,
        refundNo: recovery.bookingRefund.refundNo,
        refundType: recovery.bookingRefund.refundType,
        refundAmount: this.toNumber(recovery.bookingRefund.amount),
        originalAmount: this.toNumber(recovery.originalAmount),
        recoveryAmount: this.toNumber(recovery.recoveryAmount),
        settledAmount: this.toNumber(recovery.settledAmount),
        remainingAmount: this.toNumber(recovery.remainingAmount),
        status: recovery.status,
        reason: recovery.reason,
        createdAt: new Date(recovery.createdAt),
        applications: recovery.applications.map((application) => ({
          id: application.id,
          targetCode:
            application.bookingCommission?.commissionNo ?? application.tourCommission?.commissionNo ?? '-',
          targetType: application.targetType,
          amount: this.toNumber(application.amount),
          status: application.status,
          appliedAt: application.appliedAt ? new Date(application.appliedAt) : null,
        })),
      })),
    };
  }
}
